import { InjectionToken } from '@angular/core';

export interface OpportunityType {
  /** The backend enum String value */
  name: string;
  /** FE-Only: Localization for display */
  i18n?: string;
}

/**
 * Mapped to the BE, but beware: the BE *always* expects strings,
 * and the number values are not really in any kind of use. Can
 * be used to extract values from opportunityTypes, however.
 *
 * Degreed.Data/Enums/InternalOpportunityTypes.cs
 */
export enum OpportunityTypes {
  Permanent = 1,
  Temporary = 2,
  StretchAssignment = 3,
  Project = 4,
  Mentorship = 5,
  Secondment = 6,
  Other = 7,
  Shadowing = 8,
  DynamicTeamAssignment = 9,
  LookingForMentor = 10,
  LookingForMentee = 11,
  ReverseMentoring = 12,
  PeerMentoring = 13,
  Volunteer = 14,
  ProBono = 15,
}

/**
 * Maps closely to the backend enum for OpportunityTypes. This static list
 * should be updated whenever a new type is added.
 *
 * The addition of the `i18n` field so we can map localized display
 * names.
 *
 * @example
 * opportunityTypes(OpportunityTypes.DynamicTeamAssignment - 1)
 */
export const opportunityTypes: OpportunityType[] = [
  { name: 'Permanent', i18n: 'Opportunities_TypePermanent' },
  { name: 'Temporary', i18n: 'Opportunities_TypeTemporary' },
  {
    name: 'StretchAssignment',
    i18n: 'Opportunities_TypeStretchAssignment',
  },
  { name: 'Project', i18n: 'Opportunities_TypeProject' },
  { name: 'Mentorship', i18n: 'Opportunities_TypeMentorship' },
  { name: 'Secondment', i18n: 'Opportunities_TypeSecondment' },
  { name: 'Other', i18n: 'Opportunities_TypeOther' },
  { name: 'Shadowing', i18n: 'Opportunities_TypeShadowing' },
  {
    name: 'DynamicTeamAssignment',
    i18n: 'Opportunities_TypeDynamicTeamAssignment',
  },
  { name: 'LookingForMentor', i18n: 'Opportunities_TypeLookingForMentor' },
  { name: 'LookingForMentee', i18n: 'Opportunities_TypeLookingForMentee' },
  { name: 'ReverseMentoring', i18n: 'Opportunities_TypeReverseMentoring' },
  { name: 'PeerMentoring', i18n: 'Opportunities_TypePeerMentoring' },
  { name: 'Volunteer', i18n: 'Opportunities_TypeVolunteer' },
  { name: 'ProBono', i18n: 'Opportunities_TypeProBono' },
];

/**
 * See above. This should be an exact copy, except with the name as the
 * key.
 *
 * @example
 * opportunityTypesByName.Permanent
 * opportunityTypesByName.DynamicTeamAssignment
 */
export const opportunityTypesByName: {
  Permanent?: OpportunityType;
  Temporary?: OpportunityType;
  StretchAssignment?: OpportunityType;
  Project?: OpportunityType;
  Mentorship?: OpportunityType;
  Secondment?: OpportunityType;
  Other?: OpportunityType;
  Shadowing?: OpportunityType;
  DynamicTeamAssignment?: OpportunityType;
  LookingForMentor?: OpportunityType;
  LookingForMentee?: OpportunityType;
  ReverseMentoring?: OpportunityType;
  PeerMentoring?: OpportunityType;
  Volunteer?: OpportunityType;
  ProBono?: OpportunityType;
} = opportunityTypes.reduce((accumulator, type) => {
  return {
    ...accumulator,
    [type.name]: type,
  };
}, {});

export const OPPORTUNITY_TYPES = new InjectionToken<OpportunityType[]>(
  'OPPORTUNITY_TYPES'
);
